import React, { useEffect, useState, useRef } from 'react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { Loader2 } from 'lucide-react';

interface YouTubeChannel {
  id: string;
  name: string;
  description: string;
  customUrl?: string;
  thumbnail?: string;
  statistics?: {
    viewCount: string;
    subscriberCount: string;
    videoCount: string;
  };
}

export function YouTubeCallback() {
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [channels, setChannels] = useState<YouTubeChannel[]>([]);
  const [selectedChannel, setSelectedChannel] = useState<string | null>(null);
  const [tokenData, setTokenData] = useState<{
    access_token: string;
    refresh_token?: string;
    token_expires_at?: string;
  } | null>(null);

  // CRITICAL FIX: Prevent double execution in React Strict Mode
  const calledRef = useRef(false);

  useEffect(() => {
    // Guard against double execution
    if (calledRef.current) return;
    calledRef.current = true;

    const processYouTubeCallback = async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const errorParam = params.get('error');

      if (errorParam) {
        setError('YouTube authorization was cancelled');
        setIsProcessing(false);
        toast.error('YouTube authorization was cancelled');
        setTimeout(() => window.location.href = '/social/accounts', 2000);
        return;
      }

      if (!code) {
        setError('No authorization code received');
        setIsProcessing(false);
        toast.error('No authorization code received');
        setTimeout(() => window.location.href = '/social/accounts', 2000);
        return;
      }

      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          throw new Error('User not authenticated');
        }

        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const redirectUri = `${supabaseUrl}/functions/v1/youtube-oauth`;

        const response = await fetch(`${supabaseUrl}/functions/v1/youtube-oauth`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            code,
            redirectUri,
            userId: user.id,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to exchange authorization code');
        }

        const data = await response.json();

        if (!data.channels || data.channels.length === 0) {
          throw new Error('No YouTube channels found');
        }

        if (!data.access_token) {
          console.error('YouTube OAuth response:', data);
          throw new Error('No access token received from YouTube OAuth');
        }

        setTokenData({
          access_token: data.access_token,
          refresh_token: data.refresh_token,
          token_expires_at: data.token_expires_at,
        });
        setChannels(data.channels);
        setSelectedChannel(data.channels[0].id);
        setIsProcessing(false);

        toast.success(`Found ${data.channels.length} YouTube channel(s)!`);
      } catch (error: any) {
        console.error('YouTube callback error:', error);
        setError(error.message || 'Failed to connect YouTube account');
        setIsProcessing(false);
        toast.error(error.message || 'Failed to connect YouTube account');
      }
    };

    processYouTubeCallback();
  }, []);

  const handleSaveChannel = async () => {
    if (!selectedChannel) {
      toast.error('Please select a channel');
      return;
    }

    const channel = channels.find(c => c.id === selectedChannel);
    if (!channel) {
      toast.error('Channel not found');
      return;
    }

    if (!tokenData) {
      toast.error('Token data not available');
      return;
    }

    setIsProcessing(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      const { error: upsertError } = await supabase
        .from('social_accounts')
        .upsert({
          owner_user_id: user.id,
          platform: 'youtube',
          youtube_channel_id: channel.id,
          page_name: channel.name,
          access_token: tokenData.access_token,
          refresh_token: tokenData.refresh_token || null,
          token_expires_at: tokenData.token_expires_at || null,
          status: 'active',
        }, {
          onConflict: 'owner_user_id,platform,youtube_channel_id',
          ignoreDuplicates: false,
        });

      if (upsertError) {
        throw upsertError;
      } else {
        toast.success(`Successfully connected YouTube channel: ${channel.name}`);
      }

      const redirectUrl = localStorage.getItem('youtube_auth_redirect');
      localStorage.removeItem('youtube_auth_redirect');

      setTimeout(() => {
        window.location.href = redirectUrl || '/social/accounts';
      }, 1000);
    } catch (error: any) {
      console.error('Save channel error:', error);
      toast.error(error.message || 'Failed to save YouTube channel');
      setIsProcessing(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full">
          <div className="text-center">
            <div className="text-red-500 text-5xl mb-4">✗</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Connection Failed</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => window.location.href = '/social/accounts'}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Back to Social Accounts
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (channels.length > 0 && !isProcessing) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-2xl w-full">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Select YouTube Channel</h2>
            <p className="text-gray-600">Choose which channel you want to connect</p>
          </div>

          <div className="space-y-3 mb-6">
            {channels.map((channel) => (
              <div
                key={channel.id}
                onClick={() => setSelectedChannel(channel.id)}
                className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                  selectedChannel === channel.id
                    ? 'border-red-600 bg-red-50'
                    : 'border-gray-200 hover:border-red-300'
                }`}
              >
                <div className="flex items-start space-x-4">
                  {channel.thumbnail && (
                    <img
                      src={channel.thumbnail}
                      alt={channel.name}
                      className="w-16 h-16 rounded-full"
                    />
                  )}
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{channel.name}</h3>
                    {channel.customUrl && (
                      <p className="text-sm text-gray-500">@{channel.customUrl}</p>
                    )}
                    {channel.statistics && (
                      <div className="flex space-x-4 mt-2 text-xs text-gray-600">
                        <span>{parseInt(channel.statistics.subscriberCount).toLocaleString()} subscribers</span>
                        <span>{parseInt(channel.statistics.videoCount).toLocaleString()} videos</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex space-x-3">
            <button
              onClick={() => window.location.href = '/social/accounts'}
              className="flex-1 px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveChannel}
              disabled={!selectedChannel}
              className="flex-1 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Connect Channel
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-red-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Connecting YouTube</h2>
          <p className="text-gray-600">Please wait while we set up your YouTube integration...</p>
        </div>
      </div>
    </div>
  );
}
